import {WidgetData} from '../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../core/shared/misc/sorting';
import {QlQueryDto} from '../../../core/shared/ql/data/dto/ql-query.dto';
import {buildCwCuratedWidgetForm} from './cw-widget-form.util';

const WIDGET_ALIAS = 'config.cw.CwCaseTypeProductBipro.widget';

/**
 * Builds the "Produkt-Koordinaten" config table widget for cw.CwCaseTypeProductBipro (design
 * G.5 node id `productBipro`) — the BiPRO coordinates per product/tariff line of a
 * CwCaseTypeProduct (M2-WP1 §1, the 1:1 companion carrying Kennung/Sparte/xsi:type/risk kind).
 * Read-mostly inspection table: rows come from the alpha-cw-taa-bundle-generator SMART-Seed
 * bundle, never hand-added in the UI (wp4-fe-ruckfragen-resolved.md Q7).
 *
 * Mirrors CwConfigPage.createApplicationTemplateWidget(): WidgetFactory.createWidgetTableQl on
 * an explicit, hand-curated QlQueryDto (root cw.CwCaseTypeProductBipro aliased `e`, one LEFT
 * join to the owning caseTypeProduct — itself nested-joined to its owning caseType — so the
 * table can show both the product line AND the CaseType it belongs to) plus a curated
 * WidgetForm for explicit German column headers. Enum columns (riskKind/subTypeMechanic) are
 * selected as plain fields — the backend resolves their German label via @UiEnumValueInfo
 * metadata, no FE enum mirror needed for rendering (wp4-fe-spec.md §4.2).
 *
 * @param caseTypeProductId optional cw.CwCaseTypeProduct id to scope the table to one product
 *   line (e.g. embedded under that product's node/detail); omit for the unscoped table.
 */
export function buildCwCaseTypeProductBiproTableWidget(caseTypeProductId?: number): WidgetData {
    const qlQuery: QlQueryDto = {
        name: WIDGET_ALIAS,
        type: 'view',
        start: {
            name: 'cw.CwCaseTypeProductBipro',
            as: 'e',
            type: 'entity',
            fields: [
                {name: 'id'},
                {name: 'productKey'},         // Produktschlüssel
                {name: 'lineOfBusinessCode'}, // Sparte
                {name: 'structureVariant'},   // Strukturvariante
                {name: 'riskKind'},           // Risikoart (Enum)
                {name: 'maxRisks'},           // Max. Risiken
                {name: 'subTypeMechanic'},    // Sub-Typ-Mechanik (Enum)
                {name: 'premiumEcho'},        // Prämien-Echo
            ],
            joins: [
                {
                    type: 'entity', name: 'caseTypeProduct', as: 'ctp', joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp-Produkt (resolved NAME)
                    joins: [
                        {
                            type: 'entity', name: 'caseType', as: 'ct', joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}], // Vorgangstyp (resolved NAME)
                        },
                    ],
                },
            ],
        },
        fixedFilters: caseTypeProductId != null
            ? [FilterCriteria.create('e.caseTypeProductId', FilterCriteria.cOperatorEqual, caseTypeProductId)]
            : [],
        sortings: [new Sorting('e.productKey', true)],
    };

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: CwCaseTypeProductBipro config table — BiPRO coordinates per product/tariff line,
     *   curated columns (Vorgangstyp, Vorgangstyp-Produkt, Produktschlüssel, Sparte,
     *   Strukturvariante, Risikoart, Max. Risiken, Sub-Typ-Mechanik, Prämien-Echo).
     */
    const widgetData = WidgetFactory.createWidgetTableQl(
        WIDGET_ALIAS,
        'Produkt-Koordinaten',
        qlQuery,
        WidgetDataParam.create('widgetForm', buildCwCaseTypeProductBiproWidgetForm()),
    );
    widgetData.noDataText = 'Keine Produkt-Koordinaten konfiguriert';
    return widgetData;
}

function buildCwCaseTypeProductBiproWidgetForm() {
    return buildCwCuratedWidgetForm([
        {field: 'ct.name', uiLabel: 'Vorgangstyp'},
        {field: 'ctp.name', uiLabel: 'Vorgangstyp-Produkt'},
        {field: 'e.productKey', uiLabel: 'Produktschlüssel'},
        {field: 'e.lineOfBusinessCode', uiLabel: 'Sparte'},
        {field: 'e.structureVariant', uiLabel: 'Strukturvariante'},
        {field: 'e.riskKind', uiLabel: 'Risikoart'},
        {field: 'e.maxRisks', uiLabel: 'Max. Risiken'},
        {field: 'e.subTypeMechanic', uiLabel: 'Sub-Typ-Mechanik'},
        {field: 'e.premiumEcho', uiLabel: 'Prämien-Echo'},
    ]);
}
